// =================================================================================================
// >>> **HER ORDER, 2026-09-18: TURN ON THE BEACON FOR THE LOSSES THE SERVER CANNOT SEE.** <<<
//
// *"YES, TURN ON THE CLIENT-ERROR BEACON on the document-tool path (#278/#279) and the card-image
// path (#288/#289), so the client-side loss the server can't see is caught with the device/error,
// then fix what it surfaces."*
//
// -------------------------------------------------------------------------------------------------
// >>> **WHY THE BEACON THAT ALREADY EXISTS DOES NOT CATCH THESE.** <<<
//
// `index.html`'s snippet already records resource failures — an `<img>` that will not load fires an
// `error` on the element and the capture-phase listener files it. **But that beacon sends ONCE**, at
// 2.5 seconds or on the page leaving, and sets `sent = true` for the life of the visit.
//
// **EVERY ONE OF HER TWO TICKETS HAPPENS LONG AFTER THAT.** A member pulls cards twenty minutes into
// a conversation; a document is handed over on turn forty. The instrument was armed for the first
// two and a half seconds of a session and these losses land in the other fifty-nine minutes.
//
// -------------------------------------------------------------------------------------------------
// >>> **SO THIS IS A LATE ROW, AND IT IS A ROW RATHER THAN A SECOND SEND.** <<<
//
// The `visit` column is UNIQUE, so re-sending under the first visit id would collide and be dropped.
// Each late loss carries a FRESH visit id and `why: "late"`, which is why `arm` gained a fourth word
// rather than being widened: *the page never painted* and *a file failed an hour in* are different
// facts and must not share a row shape (R18).
//
// **AND IT IS CAPPED.** A card image failing inside a loop, or a member tapping a dead document
// eight times, must not post a book. Three per page, and then silence.
//
// **IT NEVER THROWS AND NEVER BLOCKS.** An instrument that can break the screen it measures is the
// defect wearing a lab coat (the snippet's own words). Every call is fire-and-forget, wrapped, and
// returns nothing a caller has to read.
//
// This module imports NOTHING, which is what lets a `.mjs` gate load and run it.
// =================================================================================================

/** Three per page. The fourth loss of a visit is the same story as the third. */
export const LATE_LOSS_MAX = 3;

/** The kinds of loss this reports. One word per repair, because each kind is a different fix. */
export const LOSS_KIND = Object.freeze({
  /** A card was dealt and its picture never arrived (#288/#289). */
  cardImage: "card-image",
  /** A document was handed over and would not come down (#278/#279). */
  document: "document",
  /** A message thread drew its rows and not one bubble painted (#177/#187/#287). */
  threadBubbles: "thread-bubbles",
});

export type LossKind = "card-image" | "document" | "thread-bubbles";

/**
 * >>> **IT #287 — THE LOSS THE SERVER LITERALLY CANNOT SEE.** <<<
 *
 * Arielle's three tickets (#177 08-31, #187 09-02, #287 09-17): every thread on her iPhone and
 * iPad draws the author rows and not one message bubble, while the server's read lands 200 and her
 * read stamp moves. Her own screenshots are the only instrument that ever saw it. This law decides,
 * from counts the thread view measures after paint, whether that class is on screen — so the next
 * time it happens the house learns the device and the numbers instead of waiting for a screenshot.
 *
 * **IT FIRES ONLY ON TOTAL SILENCE.** A healthy thread always has painted bubbles at the bottom
 * where it opens; rows scrolled far off screen legitimately skip layout (`content-visibility`), so
 * a count of PARTLY visible bubbles must never fire — only "messages exist and NOT ONE bubble has
 * size" is her class. An empty thread has nothing to lose and reports nothing.
 */
export function threadBubbleLoss({ messages = 0, bubbles = 0, visible = 0 }: {
  messages?: number; bubbles?: number; visible?: number;
} = {}): string | null {
  if (!(Number(messages) >= 1)) return null;
  if (Number(visible) >= 1) return null;
  return `thread drew ${Number(messages)} messages, ${Number(bubbles)} bubble boxes, 0 visible`;
}

let sentThisPage = 0;

/** Reset between tests. Never called by the app. */
export function resetLateLosses() { sentThisPage = 0; }

/** 24 chars of [a-z0-9] — the exact length `beaconUsable` refuses anything else at. */
export function freshVisitId(random: () => number = Math.random): string {
  let v = "";
  for (let i = 0; i < 24; i += 1) v += "abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(random() * 36)];
  return v;
}

/**
 * >>> **THE BODY, BUILT AS A PURE FUNCTION SO A GATE CAN READ IT WITHOUT A BROWSER.** <<<
 *
 * The shape is the snippet's, field for field, because the server's `readBeacon` is the one reader
 * and a second shape would be a second answer to what a beacon row is (R28/R29).
 *
 * **THE FAILING URL GOES IN `res`, NOT IN THE MESSAGE.** That is the list the server already keeps
 * for *a file that never arrived*, and it is what makes a broken card countable by card.
 */
export function lateLossBody({
  kind, url = "", detail = "", path = "", ua = "", visit = "",
}: {
  kind: LossKind; url?: string; detail?: string; path?: string; ua?: string; visit?: string;
}) {
  return {
    v: visit,
    p: String(path || "").slice(0, 200),
    ua: String(ua || "").slice(0, 400),
    r: "",
    ls: false, ss: false, idb: false,
    // **`painted: true` ON PURPOSE.** The app is up and working; one thing inside it did not arrive.
    // A late row that claimed the page was blank would inflate the one number she is trying to trust.
    painted: true,
    kids: 1,
    chars: 0,
    ms: 0,
    why: "late",
    errs: [{ k: "error", m: `${kind}: ${String(detail || "did not arrive")}`.slice(0, 500), s: String(url || "").slice(0, 500), l: 0, c: 0, st: "" }],
    res: url ? [{ tag: kind === LOSS_KIND.cardImage ? "IMG" : "DOC", url: String(url).slice(0, 300) }] : [],
  };
}

/**
 * >>> **REPORT ONE LOSS.** <<< Fire-and-forget, capped, and silent on every failure of its own.
 *
 * `send` and `win` are parameters for the same reason every law in this house takes its transport as
 * one: the gate drives the cap, the shape and the silence with nothing on the wire.
 */
export function reportClientLoss(
  kind: LossKind,
  { url = "", detail = "" }: { url?: string; detail?: string } = {},
  deps: {
    send?: (body: string) => void;
    path?: string;
    ua?: string;
    random?: () => number;
  } = {},
): boolean {
  try {
    if (sentThisPage >= LATE_LOSS_MAX) return false;
    sentThisPage += 1;
    const body = JSON.stringify(lateLossBody({
      kind,
      url,
      detail,
      visit: freshVisitId(deps.random),
      path: deps.path ?? (typeof location === "undefined" ? "" : location.pathname),
      ua: deps.ua ?? (typeof navigator === "undefined" ? "" : navigator.userAgent),
    }));
    const send = deps.send || defaultSend;
    send(body);
    return true;
  } catch {
    // An instrument that can break the screen it measures is the defect wearing a lab coat.
    return false;
  }
}

function defaultSend(body: string) {
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon("/api/client-error", new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch("/api/client-error", {
      method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true,
    }).catch(() => {});
  } catch { /* silence is the only safe failure for a reporter */ }
}
